import { Stack } from '@mui/material'
import { RegModalWrapper } from '../ModalContent'
import pxToRem from '../../../../helpers/pxToRem'
import styles from '../index.module.scss'
import { UserRegSx } from '../../../../components/TextField'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { UserUpdate } from '../../../../redux/userRegistrationSlice'
import { LightTooltip } from '../../../../components/Tooltip'
import {
    AccreditationDocument,
    AccreditationDocumentDetails,
    AccreditationRequiredField,
    TeacherEmploymentSetting,
} from '../../../../redux/types'
import {
    ECCQualificationLevels,
    ECCQualToTitle,
    ESQualificationOptions,
    ESQualifications,
    FieldState,
} from '../../../../firebase/types-teacher'
import { v4 as uuidv4 } from 'uuid'
import FileUpload, { FileType } from '../../../../components/FileUpload'
import { documentUploadHandler, requiredDateHandler } from './helper'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { RegFileUploadLabel } from '../../../../components/FileUpload/RegUploadComponents'
import { updateUserInfo } from '../../../../firebase/general-apis'
import { getVerifiedAccreditationDoc } from '../../helper'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import FormSelect from '../../../../components/FormComponents/FormSelect'
import FormDatePickerDayjs from '../../../../components/FormComponents/FormDatePickerDayjs'
import FormInputText from '../../../../components/FormComponents/FormInputText'
dayjs.extend(customParseFormat)

const baseSchema = z.object({
    imageURL: z.union([
        z.string().min(2),
        z.file().mime(['image/png', 'image/jpeg', 'application/pdf']),
    ]),
    reference: z.string().min(1, 'This field is required'),
})

const getImageUrl = (accred?: any, expired?: boolean): FileType | undefined => {
    if (expired) return undefined
    let imageUrl = accred?.imageURL
    if (imageUrl) {
        return { file: imageUrl, fileName: accred?.docTitle }
    } else {
        return undefined
    }
}

interface ITeachingDocumentation {
    title: string
    details: AccreditationDocumentDetails
    employmentSetting?: TeacherEmploymentSetting
}
const TeachingDocumentation = ({
    title,
    details,
    employmentSetting,
}: ITeachingDocumentation) => {
    const dispatch = useDispatch()
    const reg = useSelector((state: RootState) => state.userReg.fields)
    const userId = useSelector((state: RootState) => state.user.auth.status?.uid)

    const accredDocs = useMemo(
        () =>
            reg.accreditation?.[`${employmentSetting}`]?.[
                `${reg.setLocation?.homeState}`
            ],
        [reg.accreditation, employmentSetting, reg.setLocation?.homeState]
    )

    const document = useMemo(() => {
        return getVerifiedAccreditationDoc(accredDocs, details.type)
    }, [details.type, accredDocs])

    const strictSchema = useMemo(() => {
        let schema: z.ZodObject = baseSchema
        switch (details.required_datefield) {
            case 'registrationDate': {
                schema = schema.extend({
                    registrationDate: z.number(),
                })
                break
            }
            case 'expiryDate': {
                schema = schema.extend({
                    expiryDate: z.number(),
                })
                break
            }
        }
        switch (details.type) {
            case 'earlyChildcareQualification': {
                schema = schema.extend({
                    qualification: z.enum([
                        'certThree',
                        'dimploma',
                        'earlyChildhoodTrained',
                        'other',
                        'noFormalTraining',
                    ] as const),
                })
                break
            }
            case 'supportStaffQualification': {
                schema = schema.extend({
                    qualification: z.enum(ESQualifications),
                })
                break
            }
        }
        // Omit if no reference required
        if (details.no_reference) {
            schema = schema.omit({ reference: true })
        }
        return schema
    }, [details.type, details.required_datefield, details.no_reference])

    const looseSchema = useMemo(
        () =>
            strictSchema.partial().check((ctx) => {
                if (
                    (('expiryDate' in ctx) as any) &&
                    dayjs().isAfter(dayjs.unix((ctx as any)['expiryDate']))
                ) {
                    // Document is expired
                    ctx.issues.push({
                        code: 'custom',
                        message: 'Expiry date is in the past',
                        input: ['expiryDate'],
                    })
                }
            }),
        [strictSchema]
    )

    const {
        control,
        handleSubmit,
        formState: { isValid, errors },
        reset,
        resetField,
        setValue,
        getValues,
    } = useForm({
        resolver: zodResolver(looseSchema),
        mode: 'onChange',
    })

    const formValues = getValues()
    const isComplete = useMemo(
        () => strictSchema.safeParse(formValues).success,
        [strictSchema, formValues]
    )

    const [file, setFile] = useState<FileType | undefined>(
        getImageUrl(document)
    )
    const onFileChange = (file: any) => {
        let f =
            file instanceof File
                ? file
                : new File([file], file.name, { type: file.type })
        setFile({ file: file, fileName: file.name })
        setValue('imageURL', f, { shouldDirty: true, shouldValidate: true })
    }

    useEffect(() => {
        const baseDefaults: Record<string, any> = {
            accreditationType: document?.accreditationType || '',
            dateSubmitted: document?.dateSubmitted || '',
            docTitle: document?.docTitle || '',
            imageURL: document?.imageURL,
            type: document?.type || '',
            reference: details.no_reference ? 'N/A' : document?.reference || '',
            qualification: document?.qualification || '',
        }

        // If you have required_datefield (e.g., registrationDate, expiryDate)
        if (details?.required_datefield) {
            baseDefaults[details.required_datefield] = document?.[
                details.required_datefield
            ]
                ? dayjs(
                      document[details.required_datefield],
                      'DD-MM-YYYY'
                  ).unix()
                : undefined
        }

        reset({ ...baseDefaults })
    }, [
        document,
        details?.type,
        details?.required_datefield,
        details?.no_reference,
        reset,
    ])

    const docId = useRef(uuidv4())

    const handleUpdate = async (data: Record<string, any>) => {
        try {
            if (!userId) {
                throw new Error(
                    'User is not authenticated. Please refresh and try again.'
                )
            }

            // console.log(`Submitted data: ${JSON.stringify(data)}`)

            let updateData: UserUpdate = {}

            const datesData = requiredDateHandler(
                dayjs.unix(data[details.required_datefield as string]),
                details.required_datefield,
                details.renewal_frequency_years
            )
            const { fileUrl, fileType, docTitle } = await documentUploadHandler(
                userId,
                file,
                {
                    url: document?.imageURL,
                    type: document?.type,
                    docTitle: document?.docTitle,
                }
            )

            const isSomeComplete = Object.values(data).some(
                (v) => v !== undefined
            )

            // Normal upload
            if (document && document.id) {
                // Update data
                updateData = {
                    teacher: {
                        path: `/accreditation/${document.id}`,
                        data: {
                            ...datesData,
                            reference: details.no_reference
                                ? null
                                : data.reference || null,
                            ...(document.accreditationType
                                ? null
                                : { accreditationType: `${details.type}` }),
                            imageURL: fileUrl,
                            type: fileType,
                            docTitle: docTitle,
                            ...(isComplete
                                ? {
                                      state: 'submitted',
                                      dateSubmitted:
                                          dayjs().format('DD-MM-YYYY'),
                                  }
                                : isSomeComplete
                                  ? { state: 'incomplete' }
                                  : null),

                            ...([
                                'earlyChildcareQualification',
                                'supportStaffQualification',
                            ].includes(details.type as string)
                                ? {
                                      qualification: data.qualification || null,
                                  }
                                : null),
                        },
                    },
                }
            } else {
                // Create new document
                if (isSomeComplete) {
                    updateData = {
                        teacher: {
                            path: `/accreditation`,
                            data: {
                                [`${docId.current}`]: {
                                    accreditationType: `${details.type}`,
                                    ...datesData,
                                    dateSubmitted: dayjs().format('DD-MM-YYYY'),
                                    imageURL: fileUrl,
                                    type: fileType,
                                    docTitle: docTitle,
                                    reference: details.no_reference
                                        ? null
                                        : data.reference || null,
                                    ...(isComplete
                                        ? { state: 'submitted' }
                                        : isSomeComplete
                                          ? { state: 'incomplete' }
                                          : null),

                                    ...([
                                        'earlyChildcareQualification',
                                        'supportStaffQualification',
                                    ].includes(details.type as string)
                                        ? {
                                              qualification:
                                                  data.qualification || null,
                                          }
                                        : null),
                                },
                            },
                        },
                    }
                }
            }

            await updateUserInfo(userId, updateData)

            // dispatch(updateFields(updateData));
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    return (
        <>
            <RegModalWrapper
                title={title}
                handleUpdate={handleSubmit(handleUpdate)}
                state={document?.state}
                isAllComplete={isComplete}
                disableBtns={!isComplete}
            >
                <Stack gap={pxToRem(24)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>{details.long_title}</h5>
                        {!document?.state ||
                        document?.state === FieldState.INCOMPLETE ? (
                            <Stack className={styles.infoBox}>
                                {/* prettier-ignore */}
                                {/* <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill={styles.shades100} />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg> */}
                                {/* <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill="#C465A8" />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg> */}
                                <small>
                                    {details.details_description}
                                    <br />
                                    <br />
                                    <a
                                        href={details.website_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            textDecoration: 'underline',
                                            width: 'fit-content',
                                            color: styles.neutrals500,
                                        }}
                                    >
                                        {/* <small
                                            className={styles.p}
                                            style={{ textDecoration: 'underline' }}
                                        > */}
                                        View Website
                                        {/* </small> */}
                                    </a>
                                </small>
                            </Stack>
                        ) : (
                            <Stack
                                direction="row"
                                alignItems={'center'}
                                gap={pxToRem(12)}
                            >
                                <LightTooltip
                                    placement="bottom-start"
                                    title={
                                        <p className={styles.p}>
                                            {details.details_description}
                                            <br />
                                            <br />
                                            <a
                                                href={details.website_link}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{
                                                    textDecoration: 'none',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                <small
                                                    className={styles.p}
                                                    style={{
                                                        textDecoration:
                                                            'underline',
                                                    }}
                                                >
                                                    View Website
                                                </small>
                                            </a>
                                        </p>
                                    }
                                >
                                    {/* prettier-ignore */}
                                    <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill={styles.shades100} />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg>
                                    {/* <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill="#C465A8" />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg> */}
                                </LightTooltip>
                                <p className={styles.p}>
                                    Why we need this information
                                </p>
                            </Stack>
                        )}
                    </Stack>
                    <Stack gap={pxToRem(24)}>
                        <Stack>
                            {details.type === 'earlyChildcareQualification' ? (
                                <FormSelect
                                    sx={UserRegSx}
                                    name="qualification"
                                    control={control}
                                    options={ECCQualificationLevels.sort(
                                        (a, b) => a.localeCompare(b)
                                    ).map((el, i) => ({
                                        label: ECCQualToTitle(el),
                                        value: el,
                                    }))}
                                    props={{ label: 'Qualification' }}
                                />
                            ) : null}
                            {details.type === 'supportStaffQualification' ? (
                                <FormSelect
                                    sx={UserRegSx}
                                    name="qualification"
                                    control={control}
                                    options={ESQualificationOptions.sort(
                                        (a, b) => a.label.localeCompare(b.label)
                                    )}
                                    props={{ label: 'Qualification' }}
                                />
                            ) : null}
                        </Stack>
                        {details.required_datefield === null ? null : (
                            <FormDatePickerDayjs
                                control={control}
                                name={`${details.required_datefield}`}
                                format="DD-MM-YYYY"
                                label={getDateLabel(
                                    details?.required_datefield
                                )}
                                minDate={getMinDate(
                                    details?.required_datefield
                                )}
                                maxDate={getMaxDate(
                                    details?.required_datefield
                                )}
                                sx={UserRegSx}
                            />
                        )}
                        <FormInputText
                            control={control}
                            name={'reference'}
                            label="Reference"
                            sx={UserRegSx}
                            disabled={details.no_reference}
                        />
                        <RegFileUploadLabel
                            file={file}
                            reset={() => {
                                setFile(undefined)
                                setValue('imageURL', null, {
                                    shouldValidate: true,
                                })
                            }}
                        />
                        {!file && details.upload_instructions ? (
                            <Stack
                                direction="row"
                                alignItems={'center'}
                                gap={pxToRem(12)}
                            >
                                <LightTooltip
                                    placement="bottom-start"
                                    title={
                                        <p className={styles.p}>
                                            {details.upload_instructions}
                                        </p>
                                    }
                                >
                                    {/* prettier-ignore */}
                                    <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill={styles.shades100} />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg>
                                    {/* <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill="#C465A8" />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg> */}
                                </LightTooltip>
                                <p
                                    className={styles.p}
                                    style={{ fontSize: pxToRem(14) }}
                                >
                                    What do I upload?
                                </p>
                            </Stack>
                        ) : null}
                        <FileUpload
                            file={file}
                            onChangeCallback={onFileChange}
                            extensions={'document'}
                            iconColor={styles.primaryYellow500}
                            fileSizeLimitMb={
                                employmentSetting === 'earlyChildcare'
                                    ? null
                                    : 10
                            }
                            compressionRatio={
                                employmentSetting === 'earlyChildcare'
                                    ? null
                                    : 0.6
                            }
                        />
                    </Stack>
                </Stack>
            </RegModalWrapper>
        </>
    )
}

export const getRequiredDate = (
    accred?: AccreditationDocument,
    variant?: AccreditationRequiredField
) => {
    if (!accred) {
        return null
    }
    switch (variant) {
        case null:
            return null
        case 'registrationDate':
            return accred.registrationDate
                ? dayjs(accred.registrationDate, 'DD-MM-YYYY')
                : null
        default:
            return accred.expiryDate
                ? dayjs(accred.expiryDate, 'DD-MM-YYYY')
                : null
    }
}

export const getDateLabel = (variant?: AccreditationRequiredField) => {
    switch (variant) {
        case 'registrationDate':
            return 'Date Completed'
        // case 'expiryDate': case undefined: return 'Expiry Date';
        default:
            return 'Expiry Date'
    }
}

export const getMinDate = (variant?: AccreditationRequiredField) => {
    switch (variant) {
        case 'registrationDate':
            return undefined
        case 'expiryDate':
            return dayjs()
        default:
            return dayjs()
    }
}

export const getMaxDate = (variant?: AccreditationRequiredField) => {
    switch (variant) {
        case 'registrationDate':
            return dayjs()
        case 'expiryDate':
            return undefined
        default:
            return undefined
    }
}

export default TeachingDocumentation
